<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { advertisingApi } from '../api'
import { getToken } from '../api/session'

const props = defineProps({
  position: { type: Number, default: 0 },
  paymentMethodId: { type: Number, default: 0 },
})
const emit = defineEmits(['navigate'])
const empty = () => ({
  title: '',
  defaultDisplayType: 'LIMITED_AD',
  siteAdverts: [],
  announcements: [],
  personalAds: [],
})
const banners = ref([]),
  floats = ref([]),
  member = ref(empty()),
  now = ref(Date.now())
const bannerIndex = ref(0),
  floatIndex = ref(0),
  dismissedSiteAdIds = ref([]),
  hiddenFloats = ref({}),
  popupImageRatios = ref({})
let version = 0,
  refreshTimer,
  clockTimer,
  bannerTimer,
  floatTimer,
  lastIdentity = getToken()
const active = (items) =>
  (items || []).filter(
    (item) => !item.expiresAt || item.expiresAt * 1000 > now.value,
  )
const personalAds = computed(() => active(member.value.personalAds))
const siteAds = computed(() =>
  active(member.value.siteAdverts).filter((item) => item.imageUrl),
)
const activeSiteAd = computed(() =>
  siteAds.value.find((item) => !dismissedSiteAdIds.value.includes(item.id)),
)
const activeFloats = computed(() =>
  active(floats.value).filter(
    (item) =>
      !hiddenFloats.value[item.id] || hiddenFloats.value[item.id] <= now.value,
  ),
)
const shownFloats = computed(() =>
  activeFloats.value[0]?.rotationSeconds > 0
    ? activeFloats.value.slice(floatIndex.value, floatIndex.value + 1)
    : activeFloats.value,
)
const currentBanner = computed(() => banners.value[bannerIndex.value])
const popupVisible = computed(
  () => !props.position && Boolean(activeSiteAd.value),
)
const closable = computed(() => Boolean(activeSiteAd.value?.closable))
const popupImageRatio = computed(
  () => popupImageRatios.value[activeSiteAd.value?.imageUrl] || 0.75,
)

// 按图片比例约束弹窗，避免小图被关闭按钮遮住或长图超出视口。
function rememberPopupImageSize(imageUrl, event) {
  const { naturalWidth, naturalHeight } = event.target
  if (naturalWidth > 0 && naturalHeight > 0)
    popupImageRatios.value[imageUrl] = naturalWidth / naturalHeight
}

function closePopup() {
  if (closable.value)
    dismissedSiteAdIds.value = [
      ...dismissedSiteAdIds.value,
      activeSiteAd.value.id,
    ]
}

// 只接收站内路径或HTTP(S)链接，无链接广告不响应点击。
function open(ad) {
  const target = String(ad?.targetUrl || '').trim()
  if (!target || /[\\\r\n\t]/.test(target) || target.startsWith('//')) return
  if (target.startsWith('/')) {
    emit('navigate', target)
    return
  }
  try {
    const parsed = new URL(target)
    if (
      ['http:', 'https:'].includes(parsed.protocol) &&
      !parsed.username &&
      !parsed.password
    )
      window.location.assign(parsed.href)
  } catch {
    /* 非法地址不跳转 */
  }
}
function changeBanner(step) {
  if (banners.value.length)
    bannerIndex.value =
      (bannerIndex.value + step + banners.value.length) % banners.value.length
}
function closeFloat(ad) {
  if (!ad.closable) return
  hiddenFloats.value = {
    ...hiddenFloats.value,
    [ad.id]: Date.now() + 4 * 60 * 60 * 1000,
  }
  floatIndex.value = 0
  try {
    sessionStorage.setItem(
      'kg-web:closed-floats',
      JSON.stringify(hiddenFloats.value),
    )
  } catch {
    /* 关闭仍在本页生效 */
  }
}
async function refresh(reset = false) {
  const currentVersion = ++version,
    token = getToken(),
    position = props.position
  if (token !== lastIdentity) {
    lastIdentity = token
    member.value = empty()
    dismissedSiteAdIds.value = []
  }
  if (reset || !token) member.value = empty()
  const requests = []
  if (!position) {
    requests.push(
      advertisingApi
        .banners()
        .then((response) => {
          if (currentVersion !== version) return
          const id = currentBanner.value?.id
          banners.value = (response?.data || []).filter((item) => item.imageUrl)
          bannerIndex.value = Math.max(
            0,
            banners.value.findIndex((item) => item.id === id),
          )
        })
        .catch(() => {
          if (currentVersion === version) banners.value = []
        }),
    )
    requests.push(
      advertisingApi
        .floats()
        .then((response) => {
          if (currentVersion === version) {
            floats.value = response || []
            floatIndex.value = 0
          }
        })
        .catch(() => {
          if (currentVersion === version) floats.value = []
        }),
    )
  }
  if (token && (position !== 3 || props.paymentMethodId > 0)) {
    requests.push(
      advertisingApi
        .member(position, props.paymentMethodId)
        .then((response) => {
          if (currentVersion !== version || token !== getToken()) return
          member.value = response
        })
        .catch(() => {
          if (currentVersion === version) member.value = empty()
        }),
    )
  }
  await Promise.all(requests)
}
watch(
  () => [props.position, props.paymentMethodId, getToken()],
  () => refresh(true),
  { immediate: true },
)
watch(
  () => [
    currentBanner.value?.id,
    currentBanner.value?.rotationSeconds,
    banners.value.length,
  ],
  () => {
    clearInterval(bannerTimer)
    if (banners.value.length > 1 && currentBanner.value?.rotationSeconds > 0)
      bannerTimer = setInterval(
        () => changeBanner(1),
        currentBanner.value.rotationSeconds * 1000,
      )
  },
)
watch(
  () => [activeFloats.value.length, activeFloats.value[0]?.rotationSeconds],
  () => {
    clearInterval(floatTimer)
    floatIndex.value = 0
    if (
      activeFloats.value.length > 1 &&
      activeFloats.value[0]?.rotationSeconds > 0
    )
      floatTimer = setInterval(() => {
        floatIndex.value = (floatIndex.value + 1) % activeFloats.value.length
      }, activeFloats.value[0].rotationSeconds * 1000)
  },
)
function onVisible() {
  if (!document.hidden) refresh()
}
onMounted(() => {
  try {
    hiddenFloats.value = JSON.parse(
      sessionStorage.getItem('kg-web:closed-floats') || '{}',
    )
  } catch {
    /* 忽略损坏的关闭记录 */
  }
  refreshTimer = setInterval(() => {
    if (!document.hidden) refresh()
  }, 30000)
  clockTimer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
  document.addEventListener('visibilitychange', onVisible)
})
onBeforeUnmount(() => {
  version += 1
  ;[refreshTimer, clockTimer, bannerTimer, floatTimer].forEach(clearInterval)
  document.removeEventListener('visibilitychange', onVisible)
})
</script>

<template>
  <template v-if="!position">
    <section
      v-if="currentBanner"
      class="advertising-banner"
      aria-label="首页轮播广告"
    >
      <button
        class="advertising-banner-image"
        :aria-label="currentBanner.title"
        @click="open(currentBanner)"
      >
        <img :src="currentBanner.imageUrl" :alt="currentBanner.title" />
      </button>
      <template v-if="banners.length > 1">
        <button
          class="advertising-prev"
          aria-label="上一张广告"
          @click="changeBanner(-1)"
        >
          ‹
        </button>
        <button
          class="advertising-next"
          aria-label="下一张广告"
          @click="changeBanner(1)"
        >
          ›
        </button>
        <div class="advertising-dots">
          <button
            v-for="(banner, index) in banners"
            :key="banner.id"
            :class="{ active: index === bannerIndex }"
            :aria-label="`广告第${index + 1}张`"
            @click="bannerIndex = index"
          ></button>
        </div>
      </template>
    </section>
    <aside
      v-if="shownFloats.length"
      class="advertising-floats"
      aria-label="首页悬浮图标"
    >
      <div
        v-for="ad in shownFloats"
        :key="ad.id"
        class="advertising-float"
        :style="{ width: `${ad.width}px` }"
      >
        <button
          v-if="ad.closable"
          class="advertising-float-close"
          :aria-label="`关闭${ad.title}`"
          @click="closeFloat(ad)"
        >
          ×
        </button>
        <button :aria-label="ad.title" @click="open(ad)">
          <img
            :src="ad.imageUrl"
            :alt="ad.title"
            :style="{ width: `${ad.width}px`, height: `${ad.height}px` }"
          />
        </button>
      </div>
    </aside>
    <div v-if="popupVisible" class="advertising-mask" @click.self="closePopup">
      <section
        class="advertising-dialog"
        :style="{ '--ad-image-ratio': popupImageRatio }"
        role="dialog"
        aria-modal="true"
        :aria-label="activeSiteAd.title || member.title || '站点广告'"
      >
        <button
          v-if="closable"
          class="advertising-popup-close"
          aria-label="关闭站点广告"
          @click="closePopup"
        >
          ×
        </button>
        <button
          class="advertising-popup-image"
          :aria-label="activeSiteAd.title || '查看广告'"
          @click="open(activeSiteAd)"
        >
          <img
            :key="activeSiteAd.imageUrl"
            :src="activeSiteAd.imageUrl"
            :alt="activeSiteAd.title"
            @load="rememberPopupImageSize(activeSiteAd.imageUrl, $event)"
          />
        </button>
      </section>
    </div>
  </template>
  <section
    v-else-if="personalAds.length"
    class="advertising-personal"
    aria-label="个人中心广告"
  >
    <button
      v-for="ad in personalAds"
      :key="ad.id"
      class="advertising-card"
      @click="open(ad)"
    >
      <img :src="ad.imageUrl" :alt="ad.title" /><span>{{ ad.title }}</span>
    </button>
  </section>
</template>

<style scoped>
.advertising-banner {
  position: relative;
  background: #edf3fa;
}
.advertising-banner-image {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
}
.advertising-banner-image img {
  display: block;
  width: 100%;
  max-height: 560px;
  object-fit: contain;
}
.advertising-prev,
.advertising-next {
  position: absolute;
  top: 45%;
  border: 0;
  background: #16325d99;
  color: white;
  font-size: 36px;
  border-radius: 50%;
  width: 48px;
  height: 48px;
}
.advertising-prev {
  left: 24px;
}
.advertising-next {
  right: 24px;
}
.advertising-dots {
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  display: flex;
  gap: 8px;
  justify-content: center;
}
.advertising-dots button {
  width: 10px;
  height: 10px;
  padding: 0;
  background: #a9b7cc;
  border: 1px solid #fff;
  border-radius: 50%;
}
.advertising-dots .active {
  background: #266de5;
}
.advertising-floats {
  position: fixed;
  right: 24px;
  bottom: 80px;
  z-index: 40;
  display: grid;
  gap: 18px;
}
.advertising-float {
  position: relative;
}
.advertising-float button {
  padding: 0;
  border: 0;
  background: transparent;
}
.advertising-float img {
  display: block;
  object-fit: contain;
  max-width: 220px;
}
.advertising-float .advertising-float-close {
  position: absolute;
  top: -14px;
  right: -12px;
  background: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  box-shadow: 0 2px 8px #1d396033;
}
.advertising-mask {
  position: fixed;
  inset: 0;
  z-index: 1500;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(4px);
  display: grid;
  place-items: center;
  padding: 24px;
  box-sizing: border-box;
}
.advertising-dialog {
  position: relative;
  width: min(600px, calc(100vw - 48px), calc(78vh * var(--ad-image-ratio)));
  width: min(600px, calc(100vw - 48px), calc(78dvh * var(--ad-image-ratio)));
  overflow: hidden;
  border-radius: 24px;
  background: #101827;
  outline: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32), 0 6px 20px rgba(0, 0, 0, 0.16);
}
.advertising-popup-close {
  position: absolute;
  z-index: 2;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  color: #fff;
  font-size: 28px;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}
.advertising-popup-close:hover {
  background: rgba(15, 23, 42, 0.78);
}
.advertising-popup-close:active {
  transform: scale(0.94);
}
.advertising-popup-close:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.advertising-popup-image {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
}
.advertising-popup-image img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: var(--ad-image-ratio);
  object-fit: contain;
}
.advertising-card {
  display: block;
  width: 100%;
  padding: 0;
  margin-bottom: 16px;
  border: 0;
  border-radius: 12px;
  background: #f5f8fc;
  color: #354565;
  overflow: hidden;
  text-align: left;
}
.advertising-card img {
  display: block;
  width: 100%;
}
.advertising-card span {
  display: block;
  padding: 12px 16px;
}
.advertising-personal {
  width: 100%;
  margin: 20px 0;
}
</style>
